import { useRef, useEffect, useCallback } from "react";

interface IOptions {
  autoScroll?: boolean;
}

const useScroll = (options?: IOptions) => {
  const { autoScroll } = options || {};
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const bsRef = useRef<HTMLDivElement | null>(null);
  const preScrollTop = useRef<number>(0);
  const autoScrollRef = useRef<boolean>(!!autoScroll);
  const preContentHeightRect = useRef<number>(0);
  const scrollDirectionRef = useRef<"up" | "down" | "">("");

  const scrollViewToBottom = () => {
    if (!window.requestAnimationFrame) {
      window.requestAnimationFrame = function (callback) {
        return setTimeout(callback, 17);
      };
    }
    const target =
      bsRef.current!.scrollHeight - wrapperRef.current!.offsetHeight;
    if (bsRef.current && wrapperRef.current) {
      // 当前滚动高度
      let scrollTop = wrapperRef.current!.scrollTop;
      // 滚动step方法
      const step = function () {
        wrapperRef.current?.scrollTo(0, wrapperRef.current!.scrollHeight);
        // 距离目标滚动距离
        const distance = target - scrollTop;
        // 目标滚动位置
        scrollTop = scrollTop + distance / 5;
        if (Math.abs(distance) < 1) {
          wrapperRef.current?.scrollTo(0, target);
        } else {
          wrapperRef.current?.scrollTo(0, scrollTop);
          requestAnimationFrame(step);
        }
      };
      step();
      // bsRef.current?.scrollTo?.(0, bsRef.current.maxScrollY, 100, 'ease')
    }
  };

  const scrollViewToBottomImmediately = () => {
    if (bsRef.current && wrapperRef.current) {
      const target =
        bsRef.current!.scrollHeight - wrapperRef.current!.offsetHeight;
      wrapperRef.current!.scrollTop = target;
    }
  };

  useEffect(() => {
    if (!autoScroll) return;
    function handleScroll(event: any) {
      if (
        event.target.scrollTop < preScrollTop.current &&
        event.target.scrollTop <
          event.target.scrollHeight - event.target.offsetHeight - 100 &&
        autoScrollRef.current
      ) {
        autoScrollRef.current = false;
      }

      if (
        event.target.scrollTop >
          event.target.scrollHeight - event.target.offsetHeight - 40 &&
        !autoScrollRef.current
      ) {
        autoScrollRef.current = true;
      }

      if (event.target.scrollTop < preScrollTop.current) {
        scrollDirectionRef.current = "down";
      } else {
        scrollDirectionRef.current = "up";
      }

      preScrollTop.current = event.target.scrollTop;

      // 处理滚动事件的代码
    }
    wrapperRef.current?.addEventListener?.("scroll", handleScroll);
    return () =>
      wrapperRef.current?.removeEventListener("scroll", handleScroll);
  }, [wrapperRef.current]);

  useEffect(() => {
    if (autoScroll && bsRef.current) {
      const resizeObserver = new ResizeObserver(
        (entries: ResizeObserverEntry[]) => {
          const current = entries[0].contentRect.height;

          if (autoScrollRef.current && preContentHeightRect.current < current) {
            scrollViewToBottomImmediately();
          }
          preContentHeightRect.current = current;
        },
      );
      resizeObserver.observe(bsRef.current);

      return () => resizeObserver.disconnect();
    }
  }, [bsRef.current]);

  const init = useCallback(() => {
    autoScrollRef.current = true;
    preScrollTop.current = 0;
    preContentHeightRect.current = 0;
    scrollDirectionRef.current = "";
  }, []);

  return {
    wrapperRef,
    bsRef,
    preScrollTop,
    autoScrollRef,
    scrollDirectionRef,
    init,
    scrollViewToBottomImmediately,
    scrollViewToBottom,
  };
};

export default useScroll;
